import { ActionItem, Board, Goal, NewActionItem, NewGoal } from "@/types/board";
import { CallState } from "@/types/callState";
import { Client, Coach } from "@/types/page";
import playConfetti from "@/utils/confetti";
import { pagePropsAtom } from "@/utils/dataLayer";
import { prettyTimeSince } from "@/utils/time";
import { WebRTC } from "@/utils/WebRTC";
import axios from "axios";
import { atom, getDefaultStore } from "jotai";
import { DragEvent } from "react";
import {
    callStateAtom,
    canShowGoalsAtom,
    otherUserShowGoalsAtom,
    preparingToShareGoalsAtom,
    showGoalsAtom,
} from "../VideoRooms/VideoParts/videoAtoms";

type BoardRTCMessage =
    | {
          type: "create" | "update" | "delete";
          goals: Goal[];
      }
    | {
          type: "goalPickedUp";
          goal: Goal;
      }
    | {
          type: "goalDropped";
      }
    | {
          type: "dragOverColumn";
          columnIndex: number | null;
      }
    | {
          type: "dragOverIndex";
          index: number | null;
      }
    | {
          type: "mousePosition";
          x: number;
          y: number;
      }
    | {
          type: "editingGoal";
          goalId: number | null;
          text: string;
      }
    | {
          type: "newGoal";
          newGoal: NewGoal | null;
      }
    | {
          type: "editingActionItem";
          actionItemId: number | null;
          text: string;
      }
    | {
          type: "focusActionItem";
          actionItemId: number;
      }
    | {
          type: "blurActionItem";
          actionItemId: number;
      }
    | {
          type: "newActionItem";
          newActionItem: NewActionItem | null;
      }
    | {
          type: "createActionItems";
          actionItems: ActionItem[];
      }
    | {
          type: "updateActionItems";
          actionItems: ActionItem[];
      }
    | {
          type: "deleteActionItems";
          actionItems: ActionItem[];
      }
    | {
          type: "setShowGoals";
          showGoals: boolean;
      }
    | {
          type: "showGoalsState";
          showGoals: boolean;
      }
    | {
          type: "backgroundChange";
          background: string;
      }
    | {
          type: "triggerConfetti";
          mega: boolean;
      };

export const COLUMNS = [
    {
        key: "Future - Career",
        name: "Future - Career",
        color: "#DC1818",
    },
    {
        key: "Future - Personal",
        name: "Future - Personal",
        color: "#259998",
    },
    {
        key: "In Progress",
        name: "In Progress",
        color: "#FBA800",
    },
    {
        key: "Accomplished 🎉",
        name: "Accomplished 🎉",
        color: "#1BAA4B",
    },
] satisfies {
    key: Goal["column"];
    name: string;
    color: string;
}[];

const REBALANCED_SEPERATION_DISTANCE = 1_000_000_000;
const REBALANCE_AT_DISTANCE = 100;

export const positioningOffsets = {
    boardContainer: {
        top: 0,
        left: 0,
    },
    scrollContainer: {
        scrollTop: 0,
        scrollLeft: 0,
    },
};

export const encodeCoordinates = (x: number, y: number) => {
    return {
        x:
            x -
            positioningOffsets.boardContainer.left +
            positioningOffsets.scrollContainer.scrollLeft,
        y:
            y -
            positioningOffsets.boardContainer.top +
            positioningOffsets.scrollContainer.scrollTop,
    };
};

export const decodeCoordinates = (x: number, y: number) => {
    return {
        x: x - positioningOffsets.scrollContainer.scrollLeft,
        y: y - positioningOffsets.scrollContainer.scrollTop,
    };
};

export const playMegaConfetti = () => {
    playConfetti({
        ticks: 500,
        scalar: 3,
    });
};

export const store = getDefaultStore();

export const boardAtom = atom<Board | null>(null);
export const goalsAtom = atom<Record<Goal["id"], Goal>>({});
export const actionItemsAtom = atom<Record<ActionItem["id"], ActionItem>>({});

export const goalsByColumnAtom = atom<Record<Goal["column"], Goal[]>>((get) => {
    const columnsObj = COLUMNS.reduce(
        (acc, column) => {
            acc[column.key] = [];
            return acc;
        },
        {} as Record<Goal["column"], Goal[]>,
    );
    const columns = Object.values(get(goalsAtom)).reduce((acc, goal) => {
        acc[goal.column].push(goal);
        return acc;
    }, columnsObj);
    for (const column of Object.keys(columns) as Goal["column"][]) {
        columns[column] = columns[column].sort(
            (a, b) => a.position - b.position,
        );
    }
    return columns;
});

export const actionItemsByGoalAtom = atom<Record<Goal["id"], ActionItem[]>>(
    (get) => {
        const goalsObj = Object.values(get(goalsAtom)).reduce(
            (acc, goal) => {
                acc[goal.id] = [];
                return acc;
            },
            {} as Record<Goal["id"], ActionItem[]>,
        );
        const actionItemsByGoal = Object.values(get(actionItemsAtom)).reduce(
            (acc, actionItem) => {
                /* We need this check because if a goal is deleted, we still have its action items loaded */
                if (actionItem.goal_id in acc) {
                    acc[actionItem.goal_id].push(actionItem);
                }
                return acc;
            },
            goalsObj,
        );

        for (const goalId in actionItemsByGoal) {
            actionItemsByGoal[goalId] = actionItemsByGoal[goalId].sort(
                (a, b) => a.position - b.position,
            );
        }
        return actionItemsByGoal;
    },
);

const otherUserIdAtom = atom((get) => {
    const user = get(pagePropsAtom).user;
    const board = get(boardAtom);
    if (!user || !board)
        throw new Error(
            "Tried accessing otherUserIdAtom before board was loaded",
        );
    return user.type === "coach" ? board.client.id : board.coach.id;
});

export const userCanEditAtom = atom((get) => {
    const user = get(pagePropsAtom).user;
    return user?.type === "coach";
});

export const userCanControlNavigationAtom = atom((get) => {
    return get(userCanEditAtom) || get(callStateAtom) === CallState.Closed;
});

export const userCanSeeGoalsAssistantAtom = atom((get) => {
    const user = get(pagePropsAtom).user;
    return user?.type === "coach";
});

export const dragOverIndexAtom = atom<number | null>(null);
export const otherUserDragOverIndexAtom = atom<number | null>(null);

export const actionItemDraggedOverIndexAtom = atom<number | null>(null);

export const dragOverColumnAtom = atom<number | null>(null);
export const otherUserDragOverColumnAtom = atom<number | null>(null);

export const draggedGoalAtom = atom<Goal | null>(null);
export const otherUserDraggedGoalAtom = atom<Goal | null>(null);

export const draggedActionItemAtom = atom<ActionItem | null>(null);

export const newGoalAtom = atom<NewGoal | null>(null);
export const otherUserNewGoalAtom = atom<NewGoal | null>(null);

export const newActionItemAtom = atom<NewActionItem | null>(null);
export const otherUserNewActionItemAtom = atom<NewActionItem | null>(null);

export const editingGoalAtom = atom<number | null>(null);

export const backgroundAtom = atom(
    (get) => get(boardAtom)?.background ?? null,
    async (get, set, newBackground: string) => {
        set(boardAtom, (oldBoard) =>
            oldBoard
                ? {
                      ...oldBoard,
                      background: newBackground,
                  }
                : null,
        );
        const { data } = await axios.patch<Board>(route("board.update"), {
            other_user_id: get(otherUserIdAtom),
            background: newBackground,
        });
        set(boardAtom, data);
    },
);

export const setDraggedGoalWithSyncAtom = atom(
    null,
    (get, set, goal: Goal | null) => {
        set(draggedGoalAtom, goal);
        if (goal) {
            get(webRTCAtom).sendMessage({
                type: "goalPickedUp",
                goal,
            });
        } else {
            get(webRTCAtom).sendMessage({
                type: "goalDropped",
            });
        }
    },
);

export const otherUserConnectedAtom = atom<boolean>(false);
export const webRTCAtom = atom(
    new WebRTC<BoardRTCMessage>({
        onRoomJoined: (users) => {
            store.set(otherUserConnectedAtom, users.length > 1);
        },
        onRoomLeft: () => {
            store.set(otherUserConnectedAtom, false);
            store.set(showGoalsAtom, false);
            store.set(otherUserShowGoalsAtom, false);
            store.set(canShowGoalsAtom, false);
        },
        onUserJoined: () => {
            store.set(otherUserConnectedAtom, true);
        },
        onUserLeft: () => {
            store.set(otherUserConnectedAtom, false);
            store.set(showGoalsAtom, false);
            store.set(otherUserShowGoalsAtom, false);
            store.set(canShowGoalsAtom, false);
        },
        onConnectionStateChange: (userKey, state) => {
            store.set(canShowGoalsAtom, state === "open");
            if (state === "open") {
                store.get(webRTCAtom).sendMessage({
                    type: "showGoalsState",
                    showGoals: store.get(showGoalsAtom),
                });
            }
        },
        onMessage: (userKey, data) => {
            if (data.type === "create") {
                const newGoals = data.goals.reduce(
                    (acc, goal) => {
                        acc[goal.id] = goal;
                        return acc;
                    },
                    {} as Record<Goal["id"], Goal>,
                );
                store.set(goalsAtom, (prevGoals) => ({
                    ...prevGoals,
                    ...newGoals,
                }));
            } else if (data.type === "update") {
                store.set(goalsAtom, (prevGoals) =>
                    data.goals.reduce(
                        (acc, goal) => {
                            acc[goal.id] = goal;
                            return acc;
                        },
                        { ...prevGoals },
                    ),
                );
            } else if (data.type === "delete") {
                store.set(goalsAtom, (prevGoals) => {
                    const newGoals = { ...prevGoals };
                    for (const goalToDelete of data.goals) {
                        // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
                        delete newGoals[goalToDelete.id];
                    }
                    return newGoals;
                });
            } else if (data.type === "goalPickedUp") {
                store.set(otherUserDraggedGoalAtom, data.goal);
            } else if (data.type === "goalDropped") {
                store.set(otherUserDraggedGoalAtom, null);
            } else if (data.type === "dragOverColumn") {
                store.set(otherUserDragOverColumnAtom, data.columnIndex);
            } else if (data.type === "dragOverIndex") {
                store.set(otherUserDragOverIndexAtom, data.index);
            } else if (data.type === "editingGoal") {
                store.set(editingGoalAtom, data.goalId);
            } else if (data.type === "newGoal") {
                store.set(otherUserNewGoalAtom, data.newGoal);
            } else if (data.type === "newActionItem") {
                store.set(otherUserNewActionItemAtom, data.newActionItem);
            } else if (data.type === "triggerConfetti") {
                if (data.mega) {
                    playMegaConfetti();
                } else {
                    playConfetti();
                }
            } else if (data.type === "createActionItems") {
                const newActionItems = data.actionItems.reduce(
                    (acc, actionItem) => {
                        acc[actionItem.id] = actionItem;
                        return acc;
                    },
                    {} as Record<ActionItem["id"], ActionItem>,
                );
                store.set(actionItemsAtom, (prevActionItems) => ({
                    ...prevActionItems,
                    ...newActionItems,
                }));
            } else if (data.type === "updateActionItems") {
                store.set(actionItemsAtom, (prevActionItems) =>
                    data.actionItems.reduce(
                        (acc, actionItem) => {
                            acc[actionItem.id] = actionItem;
                            return acc;
                        },
                        { ...prevActionItems },
                    ),
                );
            } else if (data.type === "deleteActionItems") {
                store.set(actionItemsAtom, (prevActionItems) => {
                    const newActionItems = { ...prevActionItems };
                    for (const actionItemToDelete of data.actionItems) {
                        // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
                        delete newActionItems[actionItemToDelete.id];
                    }
                    return newActionItems;
                });
            } else if (data.type === "backgroundChange") {
                store.set(backgroundAtom, data.background);
            } else if (data.type === "setShowGoals") {
                store.set(showGoalsAtom, data.showGoals);
            } else if (data.type === "showGoalsState") {
                store.set(otherUserShowGoalsAtom, data.showGoals);
                store.set(preparingToShareGoalsAtom, false);
            }
        },
    }),
);

export const loadDataAtom = atom(
    null,
    async (get, set, newOtherUserId: number) => {
        const [
            { data: boardData },
            { data: goalData },
            { data: actionItemData },
        ] = await Promise.all([
            axios.get<Board>(
                route("board.get", { other_user_id: newOtherUserId }),
            ),
            axios.get<Goal[]>(
                route("board.goal.index", { other_user_id: newOtherUserId }),
            ),
            axios.get<ActionItem[]>(
                route("board.goal.action-items.index", {
                    other_user_id: newOtherUserId,
                }),
            ),
        ]);
        const goalsById = goalData.reduce(
            (acc, goal) => {
                acc[goal.id] = goal;
                return acc;
            },
            {} as Record<Goal["id"], Goal>,
        );
        const actionItemsById = actionItemData.reduce(
            (acc, actionItem) => {
                acc[actionItem.id] = actionItem;
                return acc;
            },
            {} as Record<ActionItem["id"], ActionItem>,
        );
        set(boardAtom, boardData);
        set(goalsAtom, goalsById);
        set(actionItemsAtom, actionItemsById);
    },
);

export const handleDragStartAtom = atom(
    null,
    (get, set, e: DragEvent, goalId: number, sourceIndex: number) => {
        if (e.target === null) return;
        e.dataTransfer.setData("goalId", goalId.toString());
        e.dataTransfer.setData("sourceIndex", sourceIndex.toString());

        const goal = get(goalsAtom)[goalId];
        if (goal) {
            get(webRTCAtom).sendMessage({
                type: "goalPickedUp",
                goal: goal ?? null,
            });
        }
        set(setDraggedGoalWithSyncAtom, goal ?? null);
    },
);

export const handleDragOverAtom = atom(
    null,
    (get, set, e: DragEvent, columnIndex: number, targetIndex?: number) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
        if (targetIndex !== undefined) {
            const rect = (e.target as HTMLElement).getBoundingClientRect();
            let midY = rect.top + rect.height / 2;
            // Fixes goals board flickering when a goal is dragged over a big goal
            if (get(dragOverIndexAtom) === targetIndex) {
                midY += 30;
            }
            const dropIndex = e.clientY < midY ? targetIndex : targetIndex + 1;
            set(dragOverIndexAtom, dropIndex);
            get(webRTCAtom).sendMessage({
                type: "dragOverIndex",
                index: dropIndex,
            });
        }
        set(dragOverColumnAtom, columnIndex);
        get(webRTCAtom).sendMessage({
            type: "dragOverColumn",
            columnIndex: columnIndex,
        });
    },
);

export const handleDragLeaveAtom = atom(null, (get, set, e: DragEvent) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
        set(dragOverColumnAtom, null);
        get(webRTCAtom).sendMessage({
            type: "dragOverColumn",
            columnIndex: null,
        });
    }
});

export const doDragCleanupAtom = atom(null, (get, set) => {
    document
        .querySelectorAll("[class*='ghost-goal-']")
        ?.forEach((ghostGoal) => {
            ghostGoal.remove();
        });
    set(dragOverColumnAtom, null);
    set(dragOverIndexAtom, null);
    get(webRTCAtom).sendMessage({
        type: "dragOverColumn",
        columnIndex: null,
    });
    get(webRTCAtom).sendMessage({
        type: "dragOverIndex",
        index: null,
    });
    set(setDraggedGoalWithSyncAtom, null);
});

export const handleDropAtom = atom(
    null,
    (
        get,
        set,
        e: DragEvent | number,
        targetColumnIndex: number,
        targetIndex?: number,
    ) => {
        let goalId: number;
        if (typeof e === "number") {
            goalId = e;
        } else {
            goalId = Number(e.dataTransfer.getData("goalId"));
            e.preventDefault();
        }
        if (targetIndex === undefined) return;

        set(goalsAtom, (prevGoals) => {
            const goalToMove = prevGoals[goalId];
            if (!goalToMove) return prevGoals;

            if (COLUMNS[targetColumnIndex].key === "Accomplished 🎉") {
                playMegaConfetti();
                get(webRTCAtom).sendMessage({
                    type: "triggerConfetti",
                    mega: true,
                });
            }

            const targetColumnGoals =
                get(goalsByColumnAtom)[COLUMNS[targetColumnIndex].key];

            const updatedGoal = {
                ...goalToMove,
                column: COLUMNS[targetColumnIndex].key,
            };

            const goalsToUpdate = getRebalancedItemsAfterInsertion(
                targetColumnGoals,
                updatedGoal,
                targetIndex,
            );

            set(updateGoalsAtom, Object.values(goalsToUpdate));

            return {
                ...prevGoals,
                ...goalsToUpdate,
            };
        });

        set(doDragCleanupAtom);
    },
);

export const handleDropActionItemAtom = atom(null, (get, set, e: DragEvent) => {
    e.preventDefault();
    const targetIndex = get(actionItemDraggedOverIndexAtom);
    const draggedActionItem = get(draggedActionItemAtom);
    set(actionItemDraggedOverIndexAtom, null);
    set(draggedActionItemAtom, null);
    if (targetIndex === null || draggedActionItem === null) {
        return;
    }
    set(actionItemsAtom, (prevActionItems) => {
        const targetActionItems = get(actionItemsByGoalAtom)[
            draggedActionItem.goal_id
        ];
        const actionItemsToUpdate = getRebalancedItemsAfterInsertion(
            targetActionItems,
            draggedActionItem,
            targetIndex,
        );
        set(updateActionItemsAtom, Object.values(actionItemsToUpdate));
        return {
            ...prevActionItems,
            ...actionItemsToUpdate,
        };
    });
});

export const updateGoalsAtom = atom(null, async (get, set, goals: Goal[]) => {
    get(webRTCAtom).sendMessage({
        type: "update",
        goals: goals,
    });
    try {
        await axios.patch(route("board.goal.update-many"), {
            other_user_id: get(otherUserIdAtom),
            goals: goals,
        });
    } catch (_error) {
        // showToast({
        //     message:
        //         "There was a problem saving the board. Please reload the page and try again.",
        // });
    }
});

export const createNewGoalAtom = atom(null, (get, set, columnId: number) => {
    const columnGoals = get(goalsByColumnAtom)[COLUMNS[columnId].key];
    const newGoal = {
        column: COLUMNS[columnId].key,
        title: "",
        position:
            columnGoals.length > 0
                ? Math.max(...columnGoals.map((goal) => goal.position)) +
                  REBALANCED_SEPERATION_DISTANCE
                : 0,
    };
    set(newGoalAtom, newGoal);
    get(webRTCAtom).sendMessage({
        type: "newGoal",
        newGoal: newGoal,
    });
});

export const deleteGoalAtom = atom(null, async (get, set, goalId: number) => {
    if (!confirm("Are you sure you want to delete this goal?")) return;
    set(goalsAtom, (prevGoals) => {
        const newGoals = { ...prevGoals };
        if (newGoals[goalId]) {
            get(webRTCAtom).sendMessage({
                type: "delete",
                goals: [newGoals[goalId]],
            });
        }
        // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
        delete newGoals[goalId];
        return newGoals;
    });
    try {
        await axios.delete(
            route("board.goal.delete", {
                other_user_id: get(otherUserIdAtom),
                id: goalId,
            }),
        );
    } catch (_error) {
        // showToast({
        //     message:
        //         "There was a problem saving the board. Please reload the page and try again.",
        // });
    }
});

export const saveNewGoalAtom = atom(
    null,
    async (get, set, newTitle: string) => {
        const newGoal = get(newGoalAtom);
        if (!newGoal) return;
        if (newTitle.trim() === "") {
            set(newGoalAtom, null);
            get(webRTCAtom).sendMessage({
                type: "newGoal",
                newGoal: null,
            });
            return;
        }
        try {
            const { data } = await axios.post<Goal>(
                route("board.goal.create"),
                {
                    other_user_id: get(otherUserIdAtom),
                    title: newTitle,
                    column: newGoal.column,
                    position: newGoal?.position,
                },
            );
            get(webRTCAtom).sendMessage({
                type: "create",
                goals: [data],
            });
            set(goalsAtom, (prevGoals) => ({
                ...prevGoals,
                [data.id]: data,
            }));
            set(newGoalAtom, null);
            get(webRTCAtom).sendMessage({
                type: "newGoal",
                newGoal: null,
            });
            set(editingGoalAtom, data.id);
            get(webRTCAtom).sendMessage({
                type: "editingGoal",
                goalId: data.id,
                text: data.title,
            });
        } catch (_error) {
            // showToast({
            //     message:
            //         "There was a problem saving the board. Please reload the page and try again.",
            // });
        }
    },
);

export const modifyExistingGoalAtom = atom(
    null,
    async (get, set, goalId: number, newTitle: string) => {
        if (newTitle.trim() === "") {
            set(deleteGoalAtom, goalId);
            return;
        }
        const oldGoal = get(goalsAtom)[goalId];
        if (!oldGoal || newTitle === oldGoal.title) {
            return;
        }
        const newGoal = {
            ...oldGoal,
            title: newTitle,
        };
        set(goalsAtom, (prevGoals) => ({
            ...prevGoals,
            [goalId]: newGoal,
        }));
        set(updateGoalsAtom, [newGoal]);
    },
);

export const createNewActionItemAtom = atom(
    null,
    (get, set, goalId: number) => {
        const goal = get(goalsAtom)[goalId];
        const actionItems = get(actionItemsByGoalAtom)[goalId];
        if (!goal) return;
        const newActionItem = {
            goal_id: goalId,
            title: "",
            position:
                actionItems.length > 0
                    ? Math.max(
                          ...actionItems.map(
                              (actionItem) => actionItem.position,
                          ),
                      ) + REBALANCED_SEPERATION_DISTANCE
                    : 0,
            completed_at: null,
            isSaving: false,
        };
        set(newActionItemAtom, newActionItem);
        get(webRTCAtom).sendMessage({
            type: "newActionItem",
            newActionItem: newActionItem,
        });
    },
);

export const saveNewActionItemAtom = atom(
    null,
    async (get, set, newTitle: string) => {
        const newActionItem = get(newActionItemAtom);
        if (!newActionItem) return;
        if (newTitle.trim() === "") {
            set(newActionItemAtom, null);
            get(webRTCAtom).sendMessage({
                type: "newActionItem",
                newActionItem: null,
            });
            return;
        }
        set(newActionItemAtom, { ...newActionItem, isSaving: true });
        get(webRTCAtom).sendMessage({
            type: "newActionItem",
            newActionItem: { ...newActionItem, isSaving: true },
        });
        try {
            const { data } = await axios.post<ActionItem>(
                route("board.goal.action-items.create"),
                {
                    other_user_id: get(otherUserIdAtom),
                    title: newTitle,
                    goal_id: newActionItem.goal_id,
                    position: newActionItem.position,
                    completed_at: null,
                },
            );
            get(webRTCAtom).sendMessage({
                type: "createActionItems",
                actionItems: [data],
            });
            set(actionItemsAtom, (prevActionItems) => ({
                ...prevActionItems,
                [data.id]: data,
            }));
            set(newActionItemAtom, null);
            get(webRTCAtom).sendMessage({
                type: "newActionItem",
                newActionItem: null,
            });
        } catch (_error) {
            // showToast({
            //     message:
            //         "There was a problem saving the board. Please reload the page and try again.",
            // });
        }
    },
);

export const updateActionItemsAtom = atom(
    null,
    async (get, set, actionItems: ActionItem[]) => {
        get(webRTCAtom).sendMessage({
            type: "updateActionItems",
            actionItems,
        });
        set(actionItemsAtom, (prevActionItems) =>
            actionItems.reduce(
                (acc, actionItem) => {
                    acc[actionItem.id] = actionItem;
                    return acc;
                },
                { ...prevActionItems },
            ),
        );
        try {
            await axios.patch(route("board.goal.action-items.update-many"), {
                other_user_id: get(otherUserIdAtom),
                actionItems,
            });
        } catch (_error) {
            // showToast({
            //     message:
            //         "There was a problem saving the board. Please reload the page and try again.",
            // });
        }
    },
);

export const deleteActionItemAtom = atom(
    null,
    async (get, set, actionItemId: number) => {
        if (!confirm("Are you sure you want to delete this action item?"))
            return;
        set(actionItemsAtom, (prevActionItems) => {
            const newActionItems = { ...prevActionItems };
            if (newActionItems[actionItemId]) {
                get(webRTCAtom).sendMessage({
                    type: "deleteActionItems",
                    actionItems: [newActionItems[actionItemId]],
                });
            }
            // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
            delete newActionItems[actionItemId];
            return newActionItems;
        });
        try {
            await axios.delete(
                route("board.goal.action-items.delete", {
                    other_user_id: get(otherUserIdAtom),
                    id: actionItemId,
                }),
            );
        } catch (_error) {
            // showToast({
            //     message:
            //         "There was a problem saving the board. Please reload the page and try again.",
            // });
        }
    },
);

const getRebalancedItemsAfterInsertion = <
    T extends { id: number; position: number },
>(
    targetItemsUnsorted: T[],
    initialInsertedItem: T,
    unboundedTargetIndex: number | undefined,
): Record<T["id"], T> => {
    const targetItems = targetItemsUnsorted.toSorted(
        (a, b) => a.position - b.position,
    );
    const targetIndex = Math.min(
        Math.max(unboundedTargetIndex ?? 0, 0),
        targetItems.length,
    );

    const prevNeighborPosition =
        targetItems[targetIndex - 1]?.position ??
        (targetItems[targetIndex]
            ? targetItems[targetIndex]?.position -
              REBALANCED_SEPERATION_DISTANCE
            : 0);
    const nextNeighborPosition =
        targetItems[targetIndex]?.position ??
        (targetItems[targetIndex - 1]
            ? targetItems[targetIndex - 1]?.position +
              REBALANCED_SEPERATION_DISTANCE
            : 0);

    const newPosition = Math.round(
        (prevNeighborPosition + nextNeighborPosition) / 2,
    );

    const separationDistance = Math.abs(
        (nextNeighborPosition - prevNeighborPosition) / 2,
    );

    const insertedItem = {
        ...initialInsertedItem,
        position: newPosition,
    };

    const needsRebalancing = separationDistance <= REBALANCE_AT_DISTANCE;

    if (!needsRebalancing) return { [insertedItem.id]: insertedItem };

    return targetItems
        .concat(insertedItem)
        .sort((a, b) => a.position - b.position)
        .reduce(
            (acc, item, index) => {
                acc[item.id] = {
                    ...item,
                    position: index * REBALANCED_SEPERATION_DISTANCE,
                };
                return acc;
            },
            {} as Record<number, T>,
        );
};

export const formLastUpdatedByStatement = (
    currentUser: Client | Coach,
    goalsBoard: Board,
) => {
    const boardChange = goalsBoard.last_change;
    if (!goalsBoard || !boardChange) return "Last updated • never";
    if (
        boardChange.user_type === "employee" ||
        boardChange.user_type === "system"
    ) {
        return `Last updated • ${prettyTimeSince(boardChange.changed_at)}`;
    }
    const otherUser =
        currentUser.type === "client" ? goalsBoard.coach : goalsBoard.client;
    const changedBySelf =
        currentUser.type === boardChange.user_type &&
        currentUser.id === boardChange.user_id;
    return `Last updated by ${changedBySelf ? "you" : otherUser.first_name} • ${prettyTimeSince(boardChange.changed_at)}`;
};
